import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App as AntApp, Button, Input } from 'antd';
import AppBar from '../components/AppBar';
import DocViewer from '../components/DocViewer';
import SignaturePad from '../components/SignaturePad';
import FormSheet from '../components/FormSheet';
import { AsyncButton, Chip, Skeletons, RM, dt } from '../components/kit';
import * as formsApi from '../api/coShareForms';

/**
 * FULL-SCREEN · /co-share-forms/:id — the Internal Co-Share Form.
 *
 * One screen for both sides of it, because there is only one document and the
 * only thing that differs is whose signature is missing:
 *
 *   whoever raised it → read it, issue it, chase the rest, reissue if the split
 *                       moves, void it if it was wrong
 *   every other REN   → read exactly what they are agreeing to, then sign
 *
 * The share table is the screen. Everything else on the page is the fee it
 * applies to and who still has to sign.
 */

const TONE = { DRAFT: 'brand', PENDING_SIGNING: 'warn', SIGNED: 'ok', SUPERSEDED: 'muted', VOID: 'bad' };
const LABEL = {
  DRAFT: 'Draft', PENDING_SIGNING: 'Awaiting signatures', SIGNED: 'Signed',
  SUPERSEDED: 'Superseded', VOID: 'Void',
};
// What the state means to whoever is reading, in their own terms.
const HEADLINE = {
  DRAFT: 'Not issued yet. Check the shares below, then issue it to start collecting signatures.',
  PENDING_SIGNING: null, // replaced by a live "n of m signed" line
  SIGNED: 'Every REN has signed. This is the record of how the fee is split.',
  SUPERSEDED: 'The split was varied under Clause 5 and this version was replaced. Kept as a record of what was agreed at the time.',
  VOID: 'This form was cancelled.',
};

export default function CoShareFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { message, modal } = AntApp.useApp();
  const [form, setForm] = useState(null);
  const [docHtml, setDocHtml] = useState(null);
  const [signOpen, setSignOpen] = useState(false);

  const load = () =>
    formsApi.getCoShareFormById(id)
      .then((r) => setForm(r?.data ?? r))
      .catch((e) => message.error(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openDocument = async () => {
    const r = await formsApi.getCoShareFormPreview(id);
    const html = (r?.data ?? r)?.html;
    if (!html) throw new Error('The document could not be rendered');
    setDocHtml(html);
  };

  const issue = () =>
    new Promise((resolve) => {
      modal.confirm({
        centered: true,
        title: 'Issue this co-share form?',
        content: 'The figures and shares below are fixed at this point, and every other REN is asked to sign. If the split changes afterwards you reissue rather than edit — so check it now.',
        okText: 'Issue form',
        cancelText: 'Not yet',
        onCancel: () => resolve(),
        onOk: () => formsApi.issueCoShareForm(id)
          .then(async () => { message.success('Issued — the other RENs have been notified'); await load(); resolve(); })
          .catch((e) => { message.error(e.message); resolve(); }),
      });
    });

  const sign = async (dataUrl) => {
    await formsApi.signCoShareForm(id, { signature_data_url: dataUrl });
    setSignOpen(false);
    message.success('Signed');
    await load();
  };

  const reissue = () =>
    new Promise((resolve) => {
      modal.confirm({
        centered: true,
        title: 'Reissue with the sale as it is now?',
        content: 'This version is kept as a record and marked superseded, and a new one is drafted from the sale as it stands now: its agents, their shares and its fee. Every REN signs again — which is what Clause 5 of the form requires.',
        okText: 'Reissue',
        cancelText: 'Cancel',
        onCancel: () => resolve(),
        onOk: () => formsApi.reissueCoShareForm(id)
          .then(async (r) => {
            const next = r?.data ?? r;
            message.success('New version drafted');
            resolve();
            if (next?.id) nav(`/co-share-forms/${next.id}`, { replace: true });
          })
          .catch((e) => { message.error(e.message); resolve(); }),
      });
    });

  const voidForm = () =>
    new Promise((resolve) => {
      let reason = '';
      modal.confirm({
        centered: true,
        title: 'Void this co-share form?',
        okText: 'Void form',
        okButtonProps: { danger: true },
        cancelText: 'Keep it',
        content: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 6 }}>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
              It comes off the sale and any signatures already collected are discarded. You can raise a fresh one afterwards.
            </div>
            <Input.TextArea maxLength={1000} autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Why — wrong branch, wrong split…" onChange={(e) => { reason = e.target.value; }} />
          </div>
        ),
        onCancel: () => resolve(),
        onOk: () => formsApi.voidCoShareForm(id, { reason: reason.trim() || null })
          .then(async () => { message.success('Form voided'); await load(); resolve(); })
          .catch((e) => { message.error(e.message); resolve(); }),
      });
    });

  if (!form) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar back title="Co-share form" />
        <div style={{ padding: 18 }}><Skeletons n={3} height={90} /></div>
      </div>
    );
  }

  const pending = form.signatories.filter((s) => !s.signed);
  const stale = !!form.drift?.stale;
  // "Waiting on" would chase someone who may not even be on the sale any more —
  // a stale form is waiting on a reissue, not on signatures.
  const headline = form.status === 'PENDING_SIGNING'
    ? `${form.signed_count} of ${form.signatory_count} RENs have signed.${pending.length && !stale ? ` Waiting on ${pending.map((s) => s.agent_name).join(', ')}.` : ''}`
    : HEADLINE[form.status];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        back
        title="Internal co-share form"
        sub={form.form_ref + (form.version > 1 ? ` · v${form.version}` : '')}
        right={<Chip label={LABEL[form.status] || form.status} tone={TONE[form.status]} />}
      />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* where it stands */}
        <div style={{
          background: form.my_signature_pending ? 'color-mix(in srgb,var(--warn) 10%,var(--surface))' : 'var(--surface)',
          border: `1px solid ${form.my_signature_pending ? 'color-mix(in srgb,var(--warn) 34%,var(--border))' : 'var(--border)'}`,
          borderRadius: 14, padding: '15px 16px', fontSize: 13, lineHeight: 1.55,
        }}>
          {form.my_signature_pending
            ? <><b>This is waiting on your signature.</b> Read the document, then sign at the bottom.</>
            : headline}
          {/* The reason a form was voided used to print on the document itself;
              the status is a watermark now, so the words live here. */}
          {form.status === 'VOID' && form.void_reason && (
            <div style={{ marginTop: 6, fontStyle: 'italic' }}>“{form.void_reason}”</div>
          )}
        </div>

        {/* the sale has moved away from this form — its split or its fee */}
        {stale && (
          <div style={{ background: 'color-mix(in srgb,var(--bad) 8%,var(--surface))', border: '1px solid color-mix(in srgb,var(--bad) 32%,var(--border))', borderRadius: 14, padding: '15px 16px', fontSize: 13, lineHeight: 1.55 }}>
            <b>This form no longer matches the sale</b>
            <ul style={{ margin: '6px 0 8px', paddingLeft: 18 }}>
              {(form.drift.changes?.length ? form.drift.changes.map((c) => c.text) : [form.drift.reason]).map((t) => (
                <li key={t}>{cap(t)}.</li>
              ))}
            </ul>
            Under Clause 5 a change needs new terms signed by every REN, so this version can no longer be signed. Reissue it and everyone signs the new one.
          </div>
        )}

        {/* what the percentages apply to */}
        {/* The deal is context; the fee is the subject. Keeping them in one list
            put the rental amount under a heading about sharing — and on a rental,
            where the fee is usually one month's rent, printed the same figure
            three times with an SST row between that was never subtracted. */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '4px 14px' }}>
          <Row k="Property" v={form.property_address} />
          <Row
            k={form.deal_type === 'RENT' ? 'Rent' : 'Sale price'}
            v={`${RM(form.gross_amount_rm)}${form.deal_type === 'RENT' ? '/month' : ''}`}
            last
          />
        </div>

        {/* Two shapes: a vendor-pays deal deducts nothing, so there is no sum to
            show — just the figure and why the tax is not in it. */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '4px 14px' }}>
          {form.vendor_pay_tax ? (
            <Row
              k="Professional fee, net of SST"
              v={RM(form.net_fee_rm)}
              sub={`SST @ ${form.sst_rate}% (${RM(form.sst_amount_rm)}) was billed to the ${form.deal_type === 'RENT' ? 'landlord' : 'vendor'} on top and is not part of this.`}
              accent
              last
            />
          ) : (
            <>
              <Row k="Professional fee (incl. SST)" v={RM(form.total_commission)} />
              <Row k={`Less SST @ ${form.sst_rate}%`} v={`− ${RM(form.sst_amount_rm)}`} />
              <Row k="Shared between the RENs" v={RM(form.net_fee_rm)} accent last />
            </>
          )}
        </div>

        {/* the share table — the point of the document */}
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 6 }}>
            THE SPLIT
          </div>
          <div data-tour="csf-split" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            {form.signatories.map((s, i, arr) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {s.agent_name}{s.is_me ? ' (you)' : ''}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1, fontFamily: 'ui-monospace,monospace' }}>
                    {s.ren_no || '—'}
                  </div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
                  {Number(s.share_percentage)}%
                </div>
                <div style={{ flex: 'none', width: 74, textAlign: 'right' }}>
                  {s.signed
                    ? <Chip label="Signed" tone="ok" />
                    : <Chip label={form.status === 'DRAFT' ? '—' : 'Awaiting'} tone="muted" />}
                </div>
              </div>
            ))}
          </div>
          {form.signatories.some((s) => s.signed) && (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
              {form.signatories.filter((s) => s.signed).map((s) => `${s.agent_name} signed ${dt(s.signed_at)}`).join(' · ')}
            </div>
          )}
        </div>

        <Button data-tour="csf-read" block onClick={() => openDocument().catch((e) => message.error(e.message))} style={{ height: 44, fontWeight: 600 }}>
          Read the document
        </Button>

        {/* Rare, deliberate actions live at the foot of the scroll, not beside the
            primary one. Each confirms and says what it does first. */}
        {(form.can_void || ['SIGNED', 'PENDING_SIGNING'].includes(form.status)) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            {['SIGNED', 'PENDING_SIGNING'].includes(form.status) && !stale && (
              <span onClick={reissue} style={{ alignSelf: 'center', fontSize: 13, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', padding: '6px 4px' }}>
                Split changed? Reissue this form
              </span>
            )}
            {form.can_void && (
              <span onClick={voidForm} style={{ alignSelf: 'center', fontSize: 13, fontWeight: 600, color: 'var(--bad)', cursor: 'pointer', padding: '6px 4px' }}>
                Void this form
              </span>
            )}
          </div>
        )}

        <div style={{ height: 8 }} />
      </div>

      {form.can_issue && (
        <Footer>
          <AsyncButton data-tour="csf-issue" type="primary" block size="large" onClick={issue} onError={(e) => message.error(e.message)} style={{ height: 46, fontWeight: 600 }}>
            Issue &amp; request signatures
          </AsyncButton>
        </Footer>
      )}

      {form.can_sign && (
        <Footer>
          <Button data-tour="csf-sign" type="primary" block size="large" onClick={() => setSignOpen(true)} style={{ height: 46, fontWeight: 600 }}>
            Sign this form
          </Button>
        </Footer>
      )}

      {/* In place of Sign / Issue, which the server now refuses on a stale form. */}
      {stale && form.can_reissue && (
        <Footer>
          <AsyncButton type="primary" block size="large" onClick={reissue} onError={(e) => message.error(e.message)} style={{ height: 46, fontWeight: 600 }}>
            Reissue with the sale as it is now
          </AsyncButton>
        </Footer>
      )}

      {docHtml && (
        <DocViewer title={form.form_ref} html={docHtml} onClose={() => setDocHtml(null)} />
      )}

      <FormSheet
        open={signOpen}
        title="Sign the co-share form"
        sub={`${form.form_ref} · your share is ${Number(form.signatories.find((s) => s.is_me)?.share_percentage ?? 0)}%`}
        onClose={() => setSignOpen(false)}
      >
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
          You are agreeing that the professional fee on <b style={{ color: 'var(--text)' }}>{form.sale_ref}</b>,
          net of SST, is shared as set out above — <b style={{ color: 'var(--text)' }}>{Number(form.signatories.find((s) => s.is_me)?.share_percentage ?? 0)}%</b> to you.
          Read the document first if you have not.
        </div>
        <Button block onClick={() => openDocument().catch((e) => message.error(e.message))}>Read the document</Button>
        <div data-tour="csf-sign-pad"><SignaturePad onConfirm={sign} confirmLabel="Sign form" /></div>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
          The date and time of signing are recorded against your name on the document.
        </div>
      </FormSheet>
    </div>
  );
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const Footer = ({ children }) => (
  <div style={{ flex: 'none', padding: '14px 16px', paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
    {children}
  </div>
);

/**
 * Both sides shrink and wrap. The label carries an optional second line (the SST
 * row needs one), and the value can be a full property address — so neither may
 * be `flex: none`, or the long one overflows the card instead of wrapping inside
 * it.
 */
const Row = ({ k, v, sub, accent, last }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 14, padding: '10px 0', borderBottom: last ? 'none' : '1px solid var(--border)', fontSize: 13 }}>
    <span style={{ color: 'var(--muted)', flex: '0 1 auto', minWidth: 0 }}>
      {k}
      {sub && <span style={{ display: 'block', fontSize: 11.5, opacity: 0.8, lineHeight: 1.35 }}>{sub}</span>}
    </span>
    <span style={{ fontWeight: 600, textAlign: 'right', color: accent ? 'var(--primary)' : 'var(--text)', flex: '1 1 auto', minWidth: 0, wordBreak: 'break-word' }}>{v}</span>
  </div>
);
